import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchAICapabilities,
  generateImage,
  startVideoGeneration,
  pollVideoStatus,
  type AICapabilities,
  type ImageResult,
  type VideoResult,
} from '../services/aiCreative';
import { createAd } from '../api/videos';

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

const AICreativeStudio: React.FC = () => {
  const navigate = useNavigate();
  const [caps, setCaps] = useState<AICapabilities | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingAd, setSavingAd] = useState(false);

  /* image state */
  const [imgPrompt, setImgPrompt] = useState('');
  const [imgAdTitle, setImgAdTitle] = useState('');
  const [imgSize, setImgSize] = useState('1792x1024');
  const [imgQuality, setImgQuality] = useState('hd');
  const [imgStyle, setImgStyle] = useState('vivid');
  const [imgGenerating, setImgGenerating] = useState(false);
  const [imgResult, setImgResult] = useState<ImageResult | null>(null);
  const [imgError, setImgError] = useState<string | null>(null);

  /* video state */
  const [vidPrompt, setVidPrompt] = useState('');
  const [vidAdTitle, setVidAdTitle] = useState('');
  const [vidImageUrl, setVidImageUrl] = useState('');
  const [vidNegativePrompt, setVidNegativePrompt] = useState('');
  const [vidDuration, setVidDuration] = useState<number>(5);
  const [vidAspectRatio, setVidAspectRatio] = useState('16:9');
  const [vidQuality, setVidQuality] = useState('standard');
  const [vidStyle, setVidStyle] = useState('cinematic');
  const [vidGenerating, setVidGenerating] = useState(false);
  const [vidResult, setVidResult] = useState<VideoResult | null>(null);
  const [vidError, setVidError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /* gallery of generated items this session */
  const [gallery, setGallery] = useState<
    Array<{ type: 'image' | 'video'; url: string; prompt: string }>
  >([]);

  /* fetch capabilities on mount */
  useEffect(() => {
    fetchAICapabilities()
      .then(setCaps)
      .catch(() => setCaps(null))
      .finally(() => setLoading(false));
  }, []);

  /* cleanup polling on unmount */
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  /* ---------- image generation ---------- */

  const handleGenerateImage = async () => {
    if (!imgPrompt.trim()) return;
    setImgGenerating(true);
    setImgError(null);
    setImgResult(null);
    try {
      const result = await generateImage(imgPrompt.trim(), {
        size: imgSize,
        quality: imgQuality,
        style: imgStyle,
      });
      setImgResult(result);
      setGallery((prev) => [
        { type: 'image', url: result.url, prompt: imgPrompt.trim() },
        ...prev,
      ]);
    } catch (err) {
      setImgError((err as Error).message);
    } finally {
      setImgGenerating(false);
    }
  };

  /* ---------- video generation ---------- */

  const startPolling = useCallback((taskId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await pollVideoStatus(taskId);
        setVidResult(status);
        if (status.status === 'completed') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setVidGenerating(false);
          if (status.videoUrl) {
            const videoUrl = status.videoUrl;
            setGallery((prev) => [
              { type: 'video', url: videoUrl, prompt: vidPrompt.trim() },
              ...prev,
            ]);
          }
        } else if (status.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setVidGenerating(false);
          setVidError('Video generation failed. Try a different prompt.');
        }
      } catch (err) {
        setVidError((err as Error).message);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setVidGenerating(false);
      }
    }, 5000); // poll every 5 seconds
  }, [vidPrompt]);

  const handleGenerateVideo = async () => {
    if (!vidPrompt.trim()) return;
    setVidGenerating(true);
    setVidError(null);
    setVidResult(null);
    try {
      const result = await startVideoGeneration(vidPrompt.trim(), {
        imageUrl: vidImageUrl.trim() || undefined,
        duration: vidDuration,
        aspectRatio: vidAspectRatio,
        quality: vidQuality,
        style: vidStyle,
        negativePrompt: vidNegativePrompt.trim() || undefined,
      });
      setVidResult(result);
      startPolling(result.taskId);
    } catch (err) {
      setVidError((err as Error).message);
      setVidGenerating(false);
    }
  };

  /* ---------- save as ad + navigate to campaign ---------- */

  const saveAndStartCampaign = async (
    type: 'image' | 'video',
    url: string,
    prompt: string,
    adTitle?: string,
  ) => {
    setSavingAd(true);
    try {
      const cleanTitle = adTitle?.trim();
      const promptKeywords = prompt
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((word) => word.length > 3)
        .slice(0, 5)
        .map((word) => `ai:${word}`);
      const ad = await createAd({
        title: cleanTitle || `AI ${type === 'image' ? 'Image' : 'Video'}: ${prompt.slice(0, 80)}`,
        description: `AI-generated ${type} creative.`,
        creativeType: type,
        imageUrl: type === 'image' ? url : undefined,
        videoUrl: type === 'video' ? url : undefined,
        landingUrl: undefined,
        channels: ['billboard'],
        categories: [],
        tags: ['ai-generated', ...promptKeywords],
      });
      const adId = ad?._id || ad?.id;
      navigate('/campaign/start', { state: { preSelectedAdId: adId } });
    } catch (err) {
      alert(`Failed to save creative as ad: ${(err as Error).message}`);
    } finally {
      setSavingAd(false);
    }
  };

  /* ---------- render ---------- */

  if (loading) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-8">
        <p className="text-slate-500 dark:text-slate-400">Loading AI Creative Studio...</p>
      </main>
    );
  }

  const imageEnabled = caps?.imageGeneration.enabled ?? false;
  const videoEnabled = caps?.videoGeneration.enabled ?? false;
  const nothingEnabled = !imageEnabled && !videoEnabled;

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 space-y-8">
      {/* Header */}
      <header>
        <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">AI Creative Studio</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Generate billboard-ready images and videos with AI. Describe what you want and
          the AI will create a professional creative you can use directly in your ad.
        </p>
      </header>

      {nothingEnabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <strong>AI generation is not configured yet.</strong> The admin needs to add{' '}
          <code className="rounded bg-amber-100 px-1 text-xs">OPENAI_API_KEY</code> and/or{' '}
          <code className="rounded bg-amber-100 px-1 text-xs">REPLICATE_API_TOKEN</code> to the
          server&apos;s environment variables to enable this feature.
        </div>
      )}

      {/* Image Generation */}
      <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Generate Image</h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
            DALL-E 3
          </span>
          {!imageEnabled && (
            <span className="ml-auto inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">
              Not configured
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Describe your billboard ad &mdash; include the product, mood, colors, and style you want.
          The AI will generate a high-quality image optimized for large displays.
        </p>

        <textarea
          value={imgPrompt}
          onChange={(e) => setImgPrompt(e.target.value)}
          placeholder="e.g. A vibrant summer car dealership sale, bold red and yellow colors, happy family, modern billboard format, high contrast text area on the right..."
          className="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm h-24 resize-y focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
          disabled={!imageEnabled}
        />

        <div className="mt-3">
          <label className="text-xs text-slate-600 dark:text-slate-400 block">
            Ad title (used when you click "Use in Ad")
            <input
              type="text"
              value={imgAdTitle}
              onChange={(e) => setImgAdTitle(e.target.value)}
              placeholder="e.g. Summer Car Sale Billboard"
              className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
              disabled={!imageEnabled}
            />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap gap-4 items-end">
          <label className="text-xs text-slate-600 dark:text-slate-400">
            Size
            <select
              value={imgSize}
              onChange={(e) => setImgSize(e.target.value)}
              className="mt-1 block rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm bg-white dark:bg-slate-900"
              disabled={!imageEnabled}
            >
              <option value="1792x1024">1792 x 1024 (Landscape / Billboard)</option>
              <option value="1024x1024">1024 x 1024 (Square)</option>
              <option value="1024x1792">1024 x 1792 (Portrait)</option>
            </select>
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-400">
            Quality
            <select
              value={imgQuality}
              onChange={(e) => setImgQuality(e.target.value)}
              className="mt-1 block rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm bg-white dark:bg-slate-900"
              disabled={!imageEnabled}
            >
              <option value="hd">HD (more detail)</option>
              <option value="standard">Standard (faster)</option>
            </select>
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-400">
            Style
            <select
              value={imgStyle}
              onChange={(e) => setImgStyle(e.target.value)}
              className="mt-1 block rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm bg-white dark:bg-slate-900"
              disabled={!imageEnabled}
            >
              <option value="vivid">Vivid (bold, dramatic)</option>
              <option value="natural">Natural (realistic)</option>
            </select>
          </label>
          <button
            onClick={handleGenerateImage}
            disabled={imgGenerating || !imageEnabled || !imgPrompt.trim()}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
          >
            {imgGenerating ? 'Generating...' : 'Generate Image'}
          </button>
        </div>

        {imgError && (
          <p className="mt-3 text-sm text-red-600">{imgError}</p>
        )}

        {imgResult && (
          <div className="mt-4 space-y-2">
            <img
              src={imgResult.url}
              alt="AI-generated creative"
              className="w-full max-h-96 object-contain rounded-lg border border-slate-200 dark:border-slate-700"
            />
            <p className="text-xs text-slate-400 italic">
              Revised prompt: {imgResult.revisedPrompt}
            </p>
            <div className="flex gap-2">
              <a
                href={imgResult.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50"
              >
                Open Full Size
              </a>
              <button
                onClick={() => saveAndStartCampaign('image', imgResult.url, imgPrompt, imgAdTitle)}
                disabled={savingAd}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {savingAd ? 'Saving...' : 'Use in Ad \u2192'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Video Generation */}
      <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Generate Video</h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
            {caps?.videoGeneration.provider || 'AI Video'}
          </span>
          {!videoEnabled && (
            <span className="ml-auto inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">
              Not configured
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Create short video clips for your billboard. You can optionally provide an image
          (e.g. one generated above) to turn it into a video.
        </p>

        <textarea
          value={vidPrompt}
          onChange={(e) => setVidPrompt(e.target.value)}
          placeholder="e.g. Camera slowly zooms out from a close-up of a coffee cup to reveal a cozy cafe with warm lighting, smooth cinematic movement..."
          className="w-full rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm h-24 resize-y focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
          disabled={!videoEnabled}
        />

        <div className="mt-3">
          <label className="text-xs text-slate-600 dark:text-slate-400 block">
            Ad title (used when you click "Use in Ad")
            <input
              type="text"
              value={vidAdTitle}
              onChange={(e) => setVidAdTitle(e.target.value)}
              placeholder="e.g. Grand Opening Video Spot"
              className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
              disabled={!videoEnabled}
            />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap gap-4 items-end">
          <label className="text-xs text-slate-600 dark:text-slate-400 flex-1 min-w-[200px]">
            Reference image URL (optional)
            <input
              type="text"
              value={vidImageUrl}
              onChange={(e) => setVidImageUrl(e.target.value)}
              placeholder="Paste an image URL or leave blank..."
              className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
              disabled={!videoEnabled}
            />
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-400 flex-1 min-w-[200px]">
            Negative prompt (optional)
            <input
              type="text"
              value={vidNegativePrompt}
              onChange={(e) => setVidNegativePrompt(e.target.value)}
              placeholder="e.g. jitter, morphing face, extra limbs, flicker, blur"
              className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
              disabled={!videoEnabled}
            />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap gap-4 items-end">
          <label className="text-xs text-slate-600 dark:text-slate-400">
            Duration
            <select
              value={vidDuration}
              onChange={(e) => setVidDuration(Number(e.target.value))}
              className="mt-1 block rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm bg-white dark:bg-slate-900"
              disabled={!videoEnabled}
            >
              <option value={5}>5 seconds</option>
              <option value={10}>10 seconds</option>
            </select>
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-400">
            Aspect Ratio
            <select
              value={vidAspectRatio}
              onChange={(e) => setVidAspectRatio(e.target.value)}
              className="mt-1 block rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm bg-white dark:bg-slate-900"
              disabled={!videoEnabled}
            >
              <option value="16:9">16:9 (Landscape / Billboard)</option>
              <option value="9:16">9:16 (Portrait)</option>
              <option value="1:1">1:1 (Square)</option>
            </select>
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-400">
            Quality
            <select
              value={vidQuality}
              onChange={(e) => setVidQuality(e.target.value)}
              className="mt-1 block rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm bg-white dark:bg-slate-900"
              disabled={!videoEnabled}
            >
              <option value="standard">Standard (faster)</option>
              <option value="high">High (more detail)</option>
            </select>
          </label>
          <label className="text-xs text-slate-600 dark:text-slate-400">
            Style
            <select
              value={vidStyle}
              onChange={(e) => setVidStyle(e.target.value)}
              className="mt-1 block rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm bg-white dark:bg-slate-900"
              disabled={!videoEnabled}
            >
              <option value="cinematic">Cinematic (professional, smooth)</option>
              <option value="animated">Animated (motion graphics)</option>
              <option value="natural">Natural (realistic)</option>
            </select>
          </label>
          <button
            onClick={handleGenerateVideo}
            disabled={vidGenerating || !videoEnabled || !vidPrompt.trim()}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
          >
            {vidGenerating ? 'Generating...' : 'Generate Video'}
          </button>
        </div>

        {vidError && (
          <p className="mt-3 text-sm text-red-600">{vidError}</p>
        )}

        {vidResult && (
          <div className="mt-4 space-y-2">
            {vidResult.status === 'completed' && vidResult.videoUrl ? (
              <>
                <video
                  src={vidResult.videoUrl}
                  controls
                  autoPlay
                  loop
                  muted
                  className="w-full max-h-96 rounded-lg border border-slate-200 dark:border-slate-700"
                />
                <div className="flex gap-2">
                  <a
                    href={vidResult.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50"
                  >
                    Download Video
                  </a>
                  <button
                    onClick={() => {
                      if (vidResult.videoUrl) {
                        saveAndStartCampaign('video', vidResult.videoUrl, vidPrompt, vidAdTitle);
                      }
                    }}
                    disabled={savingAd}
                    className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {savingAd ? 'Saving...' : 'Use in Ad \u2192'}
                  </button>
                </div>
              </>
            ) : vidResult.status === 'failed' ? (
              <p className="text-sm text-red-600">
                Video generation failed. Try a different prompt.
              </p>
            ) : (
              <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                <svg
                  className="h-5 w-5 animate-spin text-blue-600"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Generating your video...
                  </p>
                  <p className="text-xs text-blue-600">
                    Status: {vidResult.status} &middot; Task: {vidResult.taskId.slice(-8)}
                    &nbsp;&mdash; this usually takes 30-90 seconds.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Image-to-Video tip */}
        {imgResult && videoEnabled && (
          <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
            <strong>Tip:</strong> Want to animate your generated image? Paste the image URL
            above or
            <button
              onClick={() => {
                setVidImageUrl(imgResult.url);
              }}
              className="ml-1 underline text-blue-600 hover:text-blue-800"
            >
              click here to auto-fill it
            </button>
            , then describe the motion you want.
          </div>
        )}
      </section>

      {/* Session Gallery */}
      {gallery.length > 0 && (
        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Your Creatives ({gallery.length})
          </h2>
          <p className="text-xs text-slate-400 mb-4">
            Creatives generated during this session. Right-click to save, or use in an ad.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((item, i) => (
              <div
                key={`${item.type}-${i}`}
                className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
              >
                {item.type === 'image' ? (
                  <img
                    src={item.url}
                    alt={item.prompt.slice(0, 60)}
                    className="w-full h-40 object-cover"
                  />
                ) : (
                  <video
                    src={item.url}
                    muted
                    loop
                    autoPlay
                    className="w-full h-40 object-cover"
                  />
                )}
                <div className="p-2">
                  <p className="text-[11px] font-medium text-slate-700 dark:text-slate-300 truncate">
                    AI {item.type === 'image' ? 'Image' : 'Video'}
                  </p>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {item.prompt.split(/[\s,]+/).filter(w => w.length > 3).slice(0, 4).map((kw, ki) => (
                      <span key={ki} className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[9px] text-slate-500 dark:text-slate-400">
                        {kw.toLowerCase()}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1 flex gap-1">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-medium ${
                        item.type === 'image'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-purple-50 text-purple-700'
                      }`}
                    >
                      {item.type}
                    </span>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-slate-400 underline ml-auto"
                    >
                      open
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm text-sm text-slate-600 dark:text-slate-400">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">How It Works</h2>
        <ol className="list-decimal list-inside space-y-2">
          <li>
            <strong>Describe your ad</strong> &mdash; Write what you want the billboard to look
            like. Be specific about colors, mood, layout, and your product.
          </li>
          <li>
            <strong>AI generates it</strong> &mdash; DALL-E 3 creates a high-quality image,
            or Replicate creates a short video clip.
          </li>
          <li>
            <strong>Use in your campaign</strong> &mdash; Click &ldquo;Use in Ad&rdquo; to
            jump straight to the campaign builder with your creative ready to go.
          </li>
        </ol>
        <p className="mt-3 text-xs text-slate-400">
          Images cost ~$0.04&ndash;$0.08 each. Videos cost ~$0.05/second. Charges are billed
          to the platform&apos;s AI accounts, not your personal card.
        </p>
      </section>
    </main>
  );
};

export default AICreativeStudio;
